import { useState, useEffect } from 'react'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Target,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { dashboardService, type ExecutiveInsights, type PendingApproval, type HealthMetrics } from '../services/dashboard'
import { formatCurrency } from '../utils/apiTransformers'
import { toast } from 'sonner'
import { NeedsAttentionCard } from '../components/portfolio/NeedsAttentionCard'
import { getErrorMessage } from '../utils/apiTransformers'
import {
  MetricCard,
  AIInsightsCard,
  LeadershipActionCenter,
  ProjectStatusChart,
  CriticalProjectsList,
  TopRisksList,
  HealthMetricsGrid,
} from '../components/dashboard'
import { exportDashboardPPTX, generateDashboardReport } from '../utils/dashboardExport'

export default function Dashboard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalBudget: 0,
    atRiskProjects: 0,
    portfolioHealth: 0,
  })
  const [projectStatusData, setProjectStatusData] = useState([
    { name: 'On Track', value: 0, color: '#10b981' },
    { name: 'At Risk', value: 0, color: '#f59e0b' },
    { name: 'Critical', value: 0, color: '#ef4444' },
  ])
  const [aiInsights, setAiInsights] = useState<ExecutiveInsights>({
    working: [],
    not_working: [],
    needs_decision: [],
    emerging_concerns: [],
  })
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([])
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics | null>(null)
  const [topRisks, setTopRisks] = useState<any[]>([])
  const [criticalProjects, setCriticalProjects] = useState<any[]>([])

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // AI insights load on their own so a slow model call never holds up
      // the rest of the dashboard (2026-09-29; they're also cached server-side).
      dashboardService.getInsights()
        .then((insights) => setAiInsights(insights))
        .catch(() => { /* the insights panel keeps its empty state */ })

      // Fetch the rest in parallel
      const [
        overview,
        approvals,
        health,
        risks,
      ] = await Promise.all([
        dashboardService.getExecutiveOverview(),
        dashboardService.getPendingApprovals(),
        dashboardService.getHealthMetrics(),
        dashboardService.getRiskIndicators(),
      ])

      // Update stats from overview
      setStats({
        totalProjects: overview.summary.total_projects,
        totalBudget: overview.summary.total_budget,
        // KQA-7 — use the insights engine's at-risk rule (over budget OR
        // overdue/off-track) so the headline matches the insights list rather
        // than the unrelated on_hold/cancelled status tally.
        atRiskProjects: risks.at_risk_projects ?? 0,
        portfolioHealth: Math.round(overview.summary.average_progress),
      })

      // Update project status chart data
      setProjectStatusData([
        { name: 'On Track', value: overview.project_status_breakdown.active || 0, color: '#10b981' },
        { name: 'At Risk', value: overview.project_status_breakdown.on_hold || 0, color: '#f59e0b' },
        { name: 'Critical', value: overview.project_status_breakdown.cancelled || 0, color: '#ef4444' },
      ])

      // Update pending approvals
      setPendingApprovals(approvals.pending_approvals)

      // Update health metrics
      setHealthMetrics(health)

      // Transform risks for TopRisksList component
      // Show all risks, not just project-specific ones
      const transformedRisks = risks.risks
        .slice(0, 5)
        .map((risk, index) => ({
          id: risk.project_id || risk.type || index,
          title: risk.message.split(':')[0] || risk.message || risk.type.replace('_', ' '),
          project: risk.project_name || 'Portfolio-wide',
          impact: risk.severity === 'high' ? 'Critical' as const : risk.severity === 'medium' ? 'High' as const : 'Medium' as const,
          probability: risk.severity === 'high' ? 85 : risk.severity === 'medium' ? 65 : 45,
          financialImpact: risk.budget && risk.actual_cost 
            ? `$${((risk.actual_cost - risk.budget) / 1000).toFixed(1)}K`
            : risk.count ? `${risk.count} items` : '$0',
        }))
      setTopRisks(transformedRisks)

      // Transform risks for CriticalProjectsList component
      const criticalProjs = risks.risks
        .filter(r => r.type === 'budget_overrun' || r.type === 'schedule_delay')
        .slice(0, 5)
        .map((risk, index) => ({
          id: risk.project_id || index,
          name: risk.project_name || 'Unknown',
          issue: risk.message,
          status: 'Critical',
          budgetAtRisk: risk.budget && risk.actual_cost
            ? `$${((risk.actual_cost - risk.budget) / 1000).toFixed(1)}K`
            : '$0',
          owner: 'TBD', // Would need to fetch project manager
        }))
      setCriticalProjects(criticalProjs)
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (approval: PendingApproval) => {
    try {
      await dashboardService.approveRequest(approval.id)
      toast.success('Approval granted successfully')
      fetchDashboardData() // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to approve request')
    }
  }

  const handleReject = async (approval: PendingApproval) => {
    try {
      await dashboardService.rejectRequest(approval.id)
      toast.success('Approval rejected')
      fetchDashboardData() // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to reject request')
    }
  }

  const handleReview = async (approval: PendingApproval) => {
    try {
      await dashboardService.reviewRequest(approval.id)
      toast.success('Marked as reviewed')
      fetchDashboardData() // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to mark as reviewed')
    }
  }

  // Transform health metrics for HealthMetricsGrid component.
  //
  // Trend direction (up / down / flat) comes straight from the backend
  // and is interpreted by `colorForTrend` below per metric. We separate
  // "higher is better" (portfolio_health, delivery_confidence, schedule_stability,
  // alignment_score) from "lower is better" (cost_at_risk, decision_latency,
  // resource_stress). Pre-Sprint-18 the per-metric color logic was hand-rolled
  // and had inversions (cost_at_risk going DOWN was marked red, latency going
  // UP was marked green) — this helper makes the semantics explicit so a
  // future field can't drift.
  type TrendColor = 'green' | 'red' | 'yellow'
  const colorForTrend = (
    trend: 'up' | 'down' | 'flat',
    higherIsBetter: boolean,
  ): TrendColor => {
    if (trend === 'flat') return 'yellow' // HealthMetricsGrid hides the indicator on flat
    const isGood = (trend === 'up') === higherIsBetter
    return isGood ? 'green' : 'red'
  }
  const formatChange = (change: number, isCurrency = false) => {
    const sign = change > 0 ? '+' : ''
    const body = isCurrency ? formatCurrency(change) : `${change}`
    return `${sign}${body}`
  }

  const healthMetricsData = healthMetrics ? [
    {
      label: 'Portfolio Health Score',
      value: healthMetrics.portfolio_health_score,
      change: formatChange(healthMetrics.portfolio_health_change),
      trend: healthMetrics.portfolio_health_trend,
      color: colorForTrend(healthMetrics.portfolio_health_trend, true),
    },
    {
      label: 'Delivery Confidence',
      value: healthMetrics.delivery_confidence,
      change: formatChange(healthMetrics.delivery_confidence_change),
      trend: healthMetrics.delivery_confidence_trend,
      color: colorForTrend(healthMetrics.delivery_confidence_trend, true),
    },
    {
      label: 'Cost at Risk',
      value: formatCurrency(healthMetrics.cost_at_risk),
      change: formatChange(healthMetrics.cost_at_risk_change, true),
      trend: healthMetrics.cost_at_risk_trend,
      color: colorForTrend(healthMetrics.cost_at_risk_trend, false),
    },
    {
      label: 'Schedule Stability',
      value: healthMetrics.schedule_stability,
      change: formatChange(healthMetrics.schedule_stability_change),
      trend: healthMetrics.schedule_stability_trend,
      color: colorForTrend(healthMetrics.schedule_stability_trend, true),
    },
    {
      label: 'Decision Latency',
      value: `${healthMetrics.decision_latency_days} days`,
      change: formatChange(healthMetrics.decision_latency_change),
      trend: healthMetrics.decision_latency_trend,
      color: colorForTrend(healthMetrics.decision_latency_trend, false),
    },
    {
      label: 'Resource Stress',
      value: healthMetrics.resource_stress,
      change: formatChange(healthMetrics.resource_stress_change),
      trend: healthMetrics.resource_stress_trend,
      color: colorForTrend(healthMetrics.resource_stress_trend, false),
    },
    {
      label: 'Alignment Score',
      value: healthMetrics.alignment_score,
      change: formatChange(healthMetrics.alignment_score_change),
      trend: healthMetrics.alignment_score_trend,
      color: colorForTrend(healthMetrics.alignment_score_trend, true),
    },
  ] : []

  const buildSnapshot = () => ({
    stats,
    projectStatus: projectStatusData,
    aiInsights,
    pendingApprovals,
    healthMetrics,
    topRisks,
    criticalProjects,
  })

  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      await exportDashboardPPTX(buildSnapshot())
      toast.success('Briefing deck downloaded')
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to export presentation')
    } finally {
      setExporting(false)
    }
  }

  const handleGenerateReport = () => {
    try {
      generateDashboardReport(buildSnapshot())
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to generate report')
    }
  }

  // Transform pending approvals for LeadershipActionCenter component
  const transformedApprovals = pendingApprovals.map(approval => ({
    type: approval.type === 'stage_gate' ? 'Stage Gate' :
          approval.type === 'change_request' ? 'Change Request' :
          approval.type === 'decision' ? 'Decision' : 'Escalation',
    project: approval.project_name,
    title: approval.title,
    daysWaiting: approval.days_waiting,
    priority: approval.priority === 'critical' ? 'Critical' as const :
              approval.priority === 'high' ? 'High' as const : 'Medium' as const,
  }))

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto bg-[#f0f4f8] min-h-screen">
      {/* Page Header with Gradient */}
      <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl mb-2">Executive Command Center</h1>
            <p className="text-[#bcccdc]">Intelligence-driven portfolio oversight</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              disabled={loading || exporting}
              className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download as a PowerPoint briefing deck"
            >
              {exporting ? 'Building deck…' : 'Export to PowerPoint'}
            </button>
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              className="px-4 py-2 bg-[#DAA520] hover:bg-[#FFD700] text-[#102a43] rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* Needs attention (2026-09-28): projects with real warning signs + why */}
      <NeedsAttentionCard />

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Projects"
          value={stats.totalProjects}
          change="View all projects →"
          trend="up"
          color="green"
          loading={loading}
          onClick={() => navigate('/project')}
        />
        <MetricCard
          label="Total Investment"
          value={formatCurrency(stats.totalBudget)}
          change="View portfolio analytics →"
          trend="up"
          color="green"
          loading={loading}
          onClick={() => navigate('/portfolio/analytics')}
        />
        <MetricCard
          label="At Risk Projects"
          value={stats.atRiskProjects}
          change={stats.atRiskProjects > 0 ? 'Requires attention →' : 'All on track'}
          trend={stats.atRiskProjects > 0 ? 'down' : 'up'}
          color={stats.atRiskProjects > 0 ? 'yellow' : 'green'}
          loading={loading}
          onClick={() => navigate('/risks')}
        />
        <MetricCard
          label="Portfolio Health"
          value={`${stats.portfolioHealth}%`}
          change="View health metrics →"
          trend={stats.portfolioHealth >= 40 ? 'up' : 'down'}
          color={stats.portfolioHealth >= 70 ? 'green' : stats.portfolioHealth >= 40 ? 'yellow' : 'red'}
          loading={loading}
          onClick={() => navigate('/portfolio/dashboard')}
        />
      </div>

      {/* AI Insights - Compact Grid */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-[#102a43] font-semibold">AI Portfolio Insights</h2>
          {aiInsights.ai_powered ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#102a43] text-white">
              ✦ AI-generated
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#e8f0fe] text-[#486581]">
              Rule-based
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AIInsightsCard
          title="What's Working"
          icon={CheckCircle2}
          iconBgColor="bg-green-50"
          iconColor="text-green-600"
          items={aiInsights.working}
          itemColor="#10b981"
        />
        <AIInsightsCard
          title="What's Not Working"
          icon={XCircle}
          iconBgColor="bg-red-50"
          iconColor="text-red-600"
          items={aiInsights.not_working}
          itemColor="#ef4444"
        />
        <AIInsightsCard
          title="What Needs Your Decision"
          icon={Target}
          iconBgColor="bg-[#486581]/10"
          iconColor="text-[#486581]"
          items={aiInsights.needs_decision}
          itemColor="#486581"
        />
        <AIInsightsCard
          title="Emerging Concerns"
          icon={AlertCircle}
          iconBgColor="bg-yellow-50"
          iconColor="text-yellow-600"
          items={aiInsights.emerging_concerns}
          itemColor="#f59e0b"
        />
        </div>
      </div>

      {/* Leadership Action Center */}
      <LeadershipActionCenter
        pendingApprovals={transformedApprovals}
        onReview={(approval) => {
          const originalApproval = pendingApprovals.find(
            a => a.project_name === approval.project && a.title === approval.title
          )
          if (originalApproval) {
            handleReview(originalApproval)
          }
        }}
        onApprove={(approval) => {
          const originalApproval = pendingApprovals.find(
            a => a.project_name === approval.project && a.title === approval.title
          )
          if (originalApproval) {
            handleApprove(originalApproval)
          }
        }}
        onReject={(approval) => {
          const originalApproval = pendingApprovals.find(
            a => a.project_name === approval.project && a.title === approval.title
          )
          if (originalApproval) {
            handleReject(originalApproval)
          }
        }}
      />

      {/* Project Status Distribution */}
      <ProjectStatusChart
        data={projectStatusData}
        description={`This visualization shows the current health distribution across all ${stats.totalProjects} active projects in the portfolio.`}
      />

      {/* Critical Projects */}
      <CriticalProjectsList
        projects={criticalProjects}
        onViewAll={() => {
          navigate('/project')
        }}
        onProjectClick={(project) => {
          navigate(`/project/${project.id}`)
        }}
      />

      {/* Top Risks */}
      <TopRisksList
        risks={topRisks}
        onViewAll={() => {
          navigate('/risks')
        }}
        onRiskClick={(risk) => {
          // Only navigate if risk has a numeric project ID
          if (risk.id && typeof risk.id === 'number') {
            navigate(`/project/${risk.id}`)
          }
          // For portfolio-wide risks, navigate to risks page instead
          else if (risk.project === 'Portfolio-wide') {
            navigate('/risks')
          }
        }}
      />

      {/* Health Metrics Grid */}
      <HealthMetricsGrid metrics={healthMetricsData} />
    </div>
  )
}
